import styled from 'styled-components';

import DateFnsUtils from '@date-io/date-fns';
import {
  Button,
  KeyboardDatePicker,
  MenuItem,
  MuiPickersUtilsProvider,
  Paper,
  Select,
} from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import { DatePickerProps } from '@material-ui/pickers';
import {
  add,
  addDays,
  Duration,
  eachDayOfInterval,
  endOfDay,
  endOfISOWeek,
  endOfMonth,
  isValid,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  isEqual,
  sub,
} from 'date-fns';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSubjectContext } from '..';
import { getSubjectsActivity } from '../../../api/studykit';
import { StudyPageContext } from '../../../App';
import { usePermission } from '../../../context/authorization';
import { renderDay, renderWrappedWeekDay } from './calendar-day';

const NavigationButton = styled(Button)`
  &&& {
    width: 40px;
    min-width: unset;
    padding: 0;

    & .MuiButton-label {
      padding: 0;
    }
  }
`;

const StyledKeyboardDatePicker = styled(KeyboardDatePicker)`
  &&& .MuiInputBase-root {
    width: 192px;
  }
`;

const DatePickerPopoverPaper = styled(Paper)`
  &&& .MuiPickersCalendarHeader-dayLabel {
    margin: 0;
  }

  &&& .MuiPickersBasePicker-pickerView {
    min-width: 316px;
  }

  &&& .MuiPickersMonth-monthSelected {
    font-size: 1.2rem;
  }
`;

export type TimeRange = 'days' | 'weeks' | 'months';

const datePickerViewProps: Record<TimeRange, Partial<DatePickerProps>> = {
  days: {
    views: ['year', 'month', 'date'],
    format: 'dd-MM-yyyy',
    openTo: 'date',
    renderDay: renderDay as any,
  },
  weeks: {
    views: ['year', 'month', 'date'],
    format: "'Week of' dd MMM",
    openTo: 'date',
    renderDay: renderWrappedWeekDay as any,
  },
  months: {
    views: ['year', 'month'],
    format: 'MMMM yyyy',
    openTo: 'month',
  },
};

export interface DataPeriodPickerProps {
  date: Date;
  onDateChange: (startDate: Date, endDate: Date) => void;
  timeRangeType: TimeRange;
  onTimeRangeTypeChange?: (range: TimeRange) => void;
  fixedTimeRangeType?: boolean;
}

export function getStartOfAndEndOf(date: Date, timeRangeType: TimeRange): [Date, Date] {
  switch (timeRangeType) {
    case 'days':
      return [startOfDay(date), endOfDay(date)];
    case 'weeks':
      return [startOfISOWeek(date), endOfISOWeek(date)];
    case 'months':
      return [startOfMonth(date), endOfMonth(date)];
  }
}

export const DataPeriodPicker: React.FC<DataPeriodPickerProps> = ({
  onDateChange,
  date,
  timeRangeType,
  onTimeRangeTypeChange,
  fixedTimeRangeType,
}) => {
  const canQueryData = usePermission('SubjectsData.Query');
  const { study } = useContext(StudyPageContext);
  const { subject } = useSubjectContext();
  const [activity, setActivity] = useState<boolean[]>();

  useEffect(() => {
    const range = getStartOfAndEndOf(date, timeRangeType);
    if (!isEqual(date, range[0])) {
      onDateChange(...range);
    }
  }, [date, onDateChange, timeRangeType]);

  const handleOnMonthChange = useCallback(
    (startOfMonth: Date | null) => {
      if (!startOfMonth || !canQueryData) return;

      return getSubjectsActivity(study.id, subject.id, {
        startAt: startOfMonth,
        endAt: addDays(endOfMonth(startOfMonth), 1),
      })
        .then((r) => r[subject.id]!)
        .then(setActivity);
    },
    [canQueryData, subject.id, study.id],
  );

  useEffect(() => {
    if (!activity) handleOnMonthChange(startOfMonth(date));
  }, [date, activity, handleOnMonthChange]);

  const shouldDisableDate = useCallback(
    (d: Date | null) => {
      if (!activity || !d) return false;
      if (timeRangeType === 'months') return false;
      if (timeRangeType === 'weeks') {
        const start = startOfISOWeek(d);
        const end = endOfISOWeek(d);
        const days = eachDayOfInterval({ start, end });
        return days.every((d) => !activity[d.getDate()]);
      }

      return !activity[d.getDate()];
    },
    [activity, timeRangeType],
  );

  const handleDateChange = useCallback(
    (newDate: Date, timeRange: TimeRange) => {
      onDateChange(...getStartOfAndEndOf(newDate, timeRange));
    },
    [onDateChange],
  );

  const handleRangeTypeChange = useCallback(
    (timeRangeType: TimeRange) => {
      onTimeRangeTypeChange?.(timeRangeType);
      handleDateChange(date, timeRangeType);
    },
    [date, handleDateChange, onTimeRangeTypeChange],
  );

  const handleNextClick = useCallback(() => {
    const duration: Duration = {
      [timeRangeType]: 1,
    };
    handleDateChange(add(date, duration), timeRangeType);
  }, [timeRangeType, date, handleDateChange]);

  const handlePrevClick = useCallback(() => {
    const duration: Duration = {
      [timeRangeType]: 1,
    };
    handleDateChange(sub(date, duration), timeRangeType);
  }, [timeRangeType, date, handleDateChange]);

  return (
    <div style={{ display: 'flex', columnGap: 12, alignItems: 'center' }}>
      {!fixedTimeRangeType && (
        <Select
          className="DLSDropdown"
          style={{ padding: '0 0.75em', width: 140 }}
          defaultValue={timeRangeType}
          onChange={(e) => {
            handleRangeTypeChange(e.target.value as any);
          }}
          disabled={!canQueryData}
        >
          <MenuItem value="days">Day</MenuItem>
          <MenuItem value="weeks">Week</MenuItem>
          <MenuItem value="months">Month</MenuItem>
        </Select>
      )}
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <StyledKeyboardDatePicker
          {...datePickerViewProps[timeRangeType]}
          onMonthChange={handleOnMonthChange}
          ToolbarComponent={({ date, setOpenView, openView }) =>
            openView === 'year' ? null : (
              <div style={{ padding: '16px 16px 0 16px' }}>
                <Button fullWidth onClick={() => setOpenView('year')}>
                  {date?.getFullYear()}
                </Button>
              </div>
            )
          }
          disabled={!canQueryData}
          autoOk
          variant="inline"
          clearable={false}
          disableFuture
          shouldDisableDate={shouldDisableDate}
          value={date}
          onChange={(d) => {
            if (isValid(d)) {
              handleDateChange(d as Date, timeRangeType);
            }
          }}
          KeyboardButtonProps={{
            'aria-label': 'change date',
          }}
          PopoverProps={{
            PaperProps: {
              className: `DlsDatePicker`,
              component: DatePickerPopoverPaper,
            },
          }}
          keyboardIcon={<Icon name={'Calendar'} size={'m'} />}
          leftArrowIcon={<Icon name={'NavigationLeft'} size={'m'} />}
          rightArrowIcon={<Icon name={'NavigationRight'} size={'m'} />}
        />
      </MuiPickersUtilsProvider>
      <div>
        <NavigationButton disabled={!canQueryData} onClick={handlePrevClick}>
          <Icon name="NavigationLeft" />
        </NavigationButton>
        <NavigationButton disabled={!canQueryData} onClick={handleNextClick}>
          <Icon name="NavigationRight" />
        </NavigationButton>
      </div>
    </div>
  );
};
